<template>
  <LoadingCard :loading="loading" class="overflow-visible">
    <!-- Header -->
    <div class="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center gap-3 min-w-0">
        <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
          <svg class="h-4.5 w-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <div class="min-w-0">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">کانون‌های استانی زیرمجموعه</h3>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
            کانون‌هایی که کد کانون کشوری شما را به‌عنوان کانون بالادست دارند
          </p>
        </div>
      </div>

      <div class="flex items-center gap-2 shrink-0">
        <button
          v-if="kanoon.code"
          type="button"
          @click="copyCode"
          class="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-primary-300 dark:border-primary-700 bg-primary-50/60 dark:bg-primary-900/20 px-2.5 h-8 text-xs font-semibold text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
          :title="copied ? 'کپی شد' : 'کپی کد کانون'"
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span dir="ltr">{{ kanoon.code }}</span>
          <span v-if="copied" class="text-emerald-600 dark:text-emerald-400">✓</span>
        </button>

        <button
          @click="fetch()"
          :disabled="loading"
          class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          title="بارگذاری مجدد"
        >
          <svg :class="['h-4 w-4', loading ? 'animate-spin' : '']" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Search -->
    <div class="px-4 sm:px-6 py-3 border-b border-gray-100 dark:border-gray-700">
      <div class="relative max-w-sm">
        <input
          v-model="search"
          @input="onSearch"
          type="text"
          placeholder="جستجو بر اساس نام، کد یا شماره تماس..."
          class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 pr-9 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
        <svg class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
        </svg>
      </div>
    </div>

    <!-- Empty -->
    <div v-if="!loading && childKanoons.length === 0" class="flex flex-col items-center justify-center py-12 px-6 text-center">
      <p class="text-sm font-medium text-gray-600 dark:text-gray-300">
        {{ search ? 'کانونی با این مشخصات پیدا نشد' : 'هنوز هیچ کانون استانی زیرمجموعه شما نیست' }}
      </p>
      <p v-if="!search" class="text-xs text-gray-400 mt-1">
        کانون استانی توسط ادمین آنشو ساخته و کانون کشوری بالادست آن انتخاب می‌شود.
      </p>
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
            <th class="px-4 sm:px-6 py-2.5 text-start font-medium">کانون استانی</th>
            <th class="px-4 py-2.5 text-start font-medium">کد</th>
            <th class="px-4 py-2.5 text-start font-medium">تماس</th>
            <th class="px-4 py-2.5 text-center font-medium">آموزشگاه‌ها</th>
            <th class="px-4 py-2.5 text-start font-medium">وضعیت</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-gray-700/60">
          <tr
            v-for="child in childKanoons"
            :key="child.id"
            @click="openChild(child)"
            class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
            title="مشاهده اطلاعات پایه کانون استانی"
          >
            <td class="px-4 sm:px-6 py-3">
              <div class="flex items-center gap-2.5 min-w-0">
                <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-500">
                  {{ initial(child.name) }}
                </div>
                <span class="truncate font-semibold text-gray-900 dark:text-gray-100">{{ child.name || '—' }}</span>
              </div>
            </td>
            <td class="px-4 py-3 text-xs text-gray-500">
              <span dir="ltr">{{ child.code }}</span>
            </td>
            <td class="px-4 py-3 text-xs text-gray-500">
              <span dir="ltr">{{ child.phone || child.email || '—' }}</span>
            </td>
            <td class="px-4 py-3 text-center">
              <span class="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700/60 px-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                {{ toFa(child.institutes_count) }}
              </span>
            </td>
            <td class="px-4 py-3">
              <span
                :class="[
                  'inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium',
                  child.is_active
                    ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                    : 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300',
                ]"
              >
                {{ child.is_active ? 'فعال' : 'غیرفعال' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div
      v-if="meta.total > 0"
      class="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700"
    >
      <p class="text-xs text-gray-500 dark:text-gray-400">
        نمایش {{ toFa(meta.from) }} تا {{ toFa(meta.to) }} از {{ toFa(meta.total) }} کانون استانی
      </p>

      <div v-if="meta.last_page > 1" class="flex items-center gap-1">
        <button
          @click="goTo(meta.current_page - 1)"
          :disabled="meta.current_page <= 1 || loading"
          class="inline-flex h-8 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 text-xs text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          قبلی
        </button>

        <button
          v-for="page in pageWindow"
          :key="page"
          @click="goTo(page)"
          :disabled="loading"
          :class="[
            'inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg px-2 text-xs font-medium transition-colors',
            page === meta.current_page
              ? 'bg-primary-500 text-white'
              : 'border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50',
          ]"
        >
          {{ toFa(page) }}
        </button>

        <button
          @click="goTo(meta.current_page + 1)"
          :disabled="meta.current_page >= meta.last_page || loading"
          class="inline-flex h-8 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 text-xs text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          بعدی
        </button>
      </div>
    </div>

    <!-- Basic info popup -->
    <Modal v-if="selected" :show="true" size="lg" @close-via-escape="closeChild">
      <div class="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg">
        <div class="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div class="flex items-center gap-3 min-w-0">
            <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-sm font-semibold text-gray-500">
              {{ initial(selected.name) }}
            </div>
            <div class="min-w-0">
              <h2 class="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100 truncate">
                {{ selected.name || '—' }}
              </h2>
              <p class="text-xs text-gray-400 mt-0.5">
                <span dir="ltr">{{ selected.code }}</span> · کانون استانی
              </p>
            </div>
          </div>
          <button
            @click="closeChild"
            class="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="p-5">
          <dl class="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <div v-for="row in basicRows" :key="row.label" class="text-xs">
              <dt class="text-gray-400 mb-0.5">{{ row.label }}</dt>
              <dd class="text-gray-800 dark:text-gray-200 break-words" :dir="row.ltr ? 'ltr' : null">
                {{ row.value || '—' }}
              </dd>
            </div>
          </dl>

          <p class="mt-4 rounded-lg bg-gray-50 dark:bg-gray-700/40 px-3 py-2 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
            آموزشگاه‌های هر استان زیر نظر همان کانون استانی مدیریت می‌شوند و از این پنل قابل مشاهده نیستند.
          </p>
        </div>
      </div>
    </Modal>
  </LoadingCard>
</template>

<script>
import jMoment from 'moment-jalaali'

export default {
  name: 'KanoonChildKanoonCard',

  props: {
    card: { type: Object, required: true },
  },

  data: () => ({
    loading: true,
    childKanoons: [],
    kanoon: {},
    meta: { current_page: 1, last_page: 1, total: 0, from: 0, to: 0 },
    search: '',
    searchTimer: null,
    copied: false,
    selected: null,
  }),

  computed: {
    endpoint() {
      return this.card?.apiEndpoint ?? '/dashboard-api/kanoon/child-kanoons'
    },

    // The roster row already carries everything a national kanoon may see, so
    // the popup needs no extra request.
    basicRows() {
      const child = this.selected ?? {}

      return [
        { label: 'نام مسئول', value: child.name },
        { label: 'کد کانون', value: child.code, ltr: true },
        { label: 'شماره تماس', value: child.phone, ltr: true },
        { label: 'پست الکترونیک', value: child.email, ltr: true },
        { label: 'آموزشگاه‌های زیرمجموعه', value: this.toFa(child.institutes_count) },
        { label: 'وضعیت', value: child.is_active ? 'فعال' : 'غیرفعال' },
        { label: 'تاریخ عضویت', value: this.jalali(child.joined_at), ltr: true },
      ]
    },

    // Keep the pager to a sliding window so a long roster stays readable
    pageWindow() {
      const last = this.meta.last_page
      const current = this.meta.current_page
      const start = Math.max(1, Math.min(current - 2, last - 4))
      const end = Math.min(last, start + 4)
      const pages = []
      for (let page = start; page <= end; page++) pages.push(page)
      return pages
    },
  },

  created() {
    this.fetch()
  },

  beforeUnmount() {
    clearTimeout(this.searchTimer)
  },

  methods: {
    initial(name) {
      return (name ?? '').trim().charAt(0) || '?'
    },

    toFa(value) {
      if (value == null) return '۰'
      return Number(value).toLocaleString('fa-IR')
    },

    jalali(value) {
      if (!value) return null
      return jMoment(value, 'YYYY-MM-DD HH:mm:ss').format('jYYYY/jMM/jDD')
    },

    openChild(child) {
      this.selected = child
    },

    closeChild() {
      this.selected = null
    },

    onSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.fetch(1), 400)
    },

    goTo(page) {
      if (page < 1 || page > this.meta.last_page) return
      this.fetch(page)
    },

    async fetch(page = null) {
      this.loading = true
      try {
        const { data } = await Nova.request().get(this.endpoint, {
          params: {
            page: page ?? this.meta.current_page,
            search: this.search || undefined,
          },
        })
        this.childKanoons = data.child_kanoons ?? []
        this.kanoon = data.kanoon ?? {}
        this.meta = data.meta ?? this.meta
      } catch (error) {
        this.childKanoons = []
        Nova.error(error?.response?.data?.message ?? 'دریافت لیست کانون‌های استانی با خطا مواجه شد.')
      } finally {
        this.loading = false
      }
    },

    async copyCode() {
      try {
        await navigator.clipboard.writeText(this.kanoon.code)
        this.copied = true
        setTimeout(() => (this.copied = false), 2000)
      } catch {
        Nova.error('کپی کد کانون ممکن نشد.')
      }
    },
  },
}
</script>
